/**
 * PlatformBalanceBanner — surfaces the platform's Stripe balance at the
 * top of any admin surface where a payout may be attempted.
 *
 * Motivation (P0.16 follow-up, Anita Currie / Dallas incident): when the
 * platform Stripe balance sits at $0, every admin "Pay" click returns
 * `balance_insufficient`. Without this banner, admins bounce between the
 * shift page and Staff Payments trying to figure out why. This banner
 * fetches once, caches for 60s, and hard-links to the Add-funds flow.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { AlertTriangle, Loader2, Wallet, PlusCircle, CheckCircle2 } from 'lucide-react';
import { Button } from './ui/button';

const fmt = (n) => new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD', minimumFractionDigits: 2,
}).format(n || 0);

// Simple module-level cache — avoids hammering Stripe.Balance on every page.
let _cache = { data: null, ts: 0 };
const CACHE_MS = 60_000;

export const PlatformBalanceBanner = ({ compact = false, className = '', 'data-testid': dataTestId }) => {
  const navigate = useNavigate();
  const [balance, setBalance] = useState(_cache.data);
  const [loading, setLoading] = useState(!_cache.data);

  const load = async (force = false) => {
    const fresh = _cache.data && Date.now() - _cache.ts < CACHE_MS;
    if (fresh && !force) {
      setBalance(_cache.data);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get('/admin/stripe/platform-balance');
      _cache = { data, ts: Date.now() };
      setBalance(data);
    } catch {
      setBalance(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const wrap = (node) => (dataTestId ? <div data-testid={dataTestId}>{node}</div> : node);

  if (loading && !balance) {
    return wrap(
      <div
        className={`flex items-center gap-2 text-xs text-slate-500 px-3 py-2 ${className}`}
        data-testid="platform-balance-banner-loading"
      >
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Checking Stripe balance…
      </div>
    );
  }

  if (!balance || !balance.configured) return null;

  const available = Number(balance.available_usd || 0);
  const pending = Number(balance.pending_usd || 0);
  const empty = available <= 0.5; // <$0.50 → effectively empty (Stripe minimum)

  // Healthy state — quiet green pill so it's visible but not shouting.
  if (!empty) {
    return wrap(
      <div
        className={`flex flex-wrap items-center gap-2 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-800 px-3 py-2 text-xs ${className}`}
        data-testid="platform-balance-banner-ok"
      >
        <CheckCircle2 className="w-4 h-4 text-emerald-600" />
        <span className="font-semibold">Stripe balance:</span>
        <span data-testid="platform-balance-available">{fmt(available)}</span>
        <span className="text-emerald-700/70">available</span>
        {pending > 0 && (
          <span className="text-emerald-700/70 ml-1">
            · {fmt(pending)} pending
          </span>
        )}
        {!compact && (
          <Button
            size="sm"
            variant="ghost"
            className="h-6 px-2 text-emerald-800 hover:bg-emerald-100 ml-auto"
            onClick={() => navigate('/admin/staff-payments')}
            data-testid="platform-balance-manage-btn"
          >
            Manage
          </Button>
        )}
      </div>
    );
  }

  // Empty / low — loud amber banner with primary CTA to Add Funds.
  return wrap(
    <div
      className={`flex flex-wrap items-center gap-2 rounded-md bg-amber-50 border-l-4 border-amber-500 text-amber-900 px-3 py-2.5 text-sm ${className}`}
      data-testid="platform-balance-banner-empty"
    >
      <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0" />
      <div className="flex-1 min-w-[220px]">
        <div className="font-semibold" data-testid="platform-balance-empty-title">
          Platform Stripe balance: {fmt(available)}
        </div>
        <div className="text-xs text-amber-800/90">
          Payouts to talent will fail with <code className="font-mono">balance_insufficient</code> until you top up.
          {pending > 0 && <> ({fmt(pending)} in transit, not spendable yet.)</>}
        </div>
      </div>
      <Button
        size="sm"
        className="bg-amber-600 hover:bg-amber-700 text-white"
        onClick={() => navigate('/admin/staff-payments')}
        data-testid="platform-balance-add-funds-btn"
      >
        <PlusCircle className="w-3.5 h-3.5 mr-1" /> Add funds
      </Button>
      {!compact && (
        <Button
          size="sm"
          variant="outline"
          className="border-amber-300 text-amber-900 hover:bg-amber-100"
          onClick={() => navigate('/admin/payouts/failed')}
          data-testid="platform-balance-view-failed-btn"
        >
          <Wallet className="w-3.5 h-3.5 mr-1" /> View failed payouts
        </Button>
      )}
    </div>
  );
};

export default PlatformBalanceBanner;
